import {
  Activity,
  ChevronLeft,
  Compass,
  Cookie,
  Download,
  FolderOpen,
  Layers,
  LayoutDashboard,
  Library,
  Lock,
  MessageSquare,
  Scissors,
  Users,
  Settings2,
} from 'lucide-react';
import { NavLink, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import { useAuth } from '@/domains/identity/AuthProvider';
import { cn } from '@/shared/lib/cn';
import { Tooltip } from '@/shared/ui';
import { FEATURE_KEYS, routes } from '../routes';
import { WorkspaceSwitcher } from '@/domains/workspaces/WorkspaceSwitcher';

export interface NavItem {
  to: string;
  labelKey: string;
  icon: React.ReactNode;
  featureKey?: string;
  adminOnly?: boolean;
  /** Match child routes too (e.g. /projects/123 highlights Projects). */
  matchPrefix?: string;
}

const WORK_ITEMS: NavItem[] = [
  { to: routes.overview, labelKey: 'nav.overview', icon: <LayoutDashboard className="h-4 w-4" /> },
  { to: routes.download, labelKey: 'nav.download', icon: <Download className="h-4 w-4" /> },
  {
    to: routes.batch,
    labelKey: 'nav.batch',
    icon: <Layers className="h-4 w-4" />,
    featureKey: FEATURE_KEYS.editor,
    matchPrefix: '/batch',
  },
  {
    to: routes.explorer,
    labelKey: 'nav.explorer',
    icon: <Compass className="h-4 w-4" />,
    featureKey: FEATURE_KEYS.explorer,
    matchPrefix: '/explorer',
  },
  {
    to: routes.projects,
    labelKey: 'nav.projects',
    icon: <Scissors className="h-4 w-4" />,
    featureKey: FEATURE_KEYS.editor,
    matchPrefix: '/projects',
  },
];

const RESULT_ITEMS: NavItem[] = [
  {
    to: routes.libraryDownloads,
    labelKey: 'nav.library',
    icon: <Library className="h-4 w-4" />,
    matchPrefix: '/library',
  },
  { to: routes.activity, labelKey: 'nav.activity', icon: <Activity className="h-4 w-4" /> },
  { to: routes.workspaces, labelKey: 'nav.workspaces', icon: <Settings2 className="h-4 w-4" /> },
];

const ADMIN_ITEMS: NavItem[] = [
  { to: routes.adminUsers, labelKey: 'nav.adminUsers', icon: <Users className="h-4 w-4" />, adminOnly: true, matchPrefix: '/admin/users' },
  { to: routes.adminIntegrations, labelKey: 'nav.adminIntegrations', icon: <Cookie className="h-4 w-4" />, adminOnly: true },
  { to: routes.adminFeedback, labelKey: 'nav.adminFeedback', icon: <MessageSquare className="h-4 w-4" />, adminOnly: true },
];

function SidebarLink({ item, collapsed }: { item: NavItem; collapsed: boolean }) {
  const { t } = useTranslation();
  const { hasFeature } = useAuth();
  const location = useLocation();

  const label = t(item.labelKey);
  // A feature the user lacks still appears, with a lock — hiding it would leave people unable
  // to discover (or ask for) what the product can do.
  const locked = Boolean(item.featureKey) && !hasFeature(item.featureKey!);
  const active = item.matchPrefix
    ? location.pathname === item.to || location.pathname.startsWith(`${item.matchPrefix}/`) || location.pathname === item.matchPrefix
    : location.pathname === item.to;

  const content = (
    <NavLink
      to={item.to}
      aria-current={active ? 'page' : undefined}
      // Collapsed, the link renders only a decorative icon — the tooltip is a visual
      // affordance, not an accessible name, so the name is set explicitly.
      aria-label={collapsed ? (locked ? `${label} — ${t('nav.locked')}` : label) : undefined}
      className={cn(
        'group relative flex items-center gap-2.5 rounded px-2.5 py-2 text-base font-medium',
        'cursor-pointer transition-colors duration-150',
        active ? 'bg-surface-hover text-fg' : 'text-fg-muted hover:bg-surface-hover hover:text-fg',
        collapsed && 'justify-center px-0',
      )}
    >
      {/* Active state is colour + a bar, never colour alone. */}
      <span
        className={cn(
          'absolute left-0 top-1/2 h-4 w-0.5 -translate-y-1/2 rounded-r-full bg-accent transition-opacity',
          active ? 'opacity-100' : 'opacity-0',
        )}
        aria-hidden
      />
      <span className="shrink-0">{item.icon}</span>
      {!collapsed && <span className="min-w-0 flex-1 truncate">{label}</span>}
      {!collapsed && locked && (
        <Lock className="h-3 w-3 shrink-0 text-fg-subtle" aria-label={t('nav.locked')} />
      )}
    </NavLink>
  );

  return collapsed ? (
    <Tooltip content={locked ? `${label} — ${t('nav.locked')}` : label} side="right">
      {content}
    </Tooltip>
  ) : (
    content
  );
}

function Group({
  titleKey,
  items,
  collapsed,
}: {
  titleKey: string;
  items: NavItem[];
  collapsed: boolean;
}) {
  const { t } = useTranslation();
  if (items.length === 0) return null;
  return (
    <div className="space-y-0.5">
      {!collapsed && (
        <p className="px-2.5 pb-1 pt-3 text-2xs font-semibold uppercase tracking-wider text-fg-subtle">
          {t(titleKey)}
        </p>
      )}
      {collapsed && <div className="my-2 h-px bg-border" aria-hidden />}
      {items.map((item) => (
        <SidebarLink key={item.to} item={item} collapsed={collapsed} />
      ))}
    </div>
  );
}

export function Sidebar({
  collapsed,
  onToggle,
  hideToggle,
  embedded,
}: {
  collapsed: boolean;
  onToggle: () => void;
  hideToggle?: boolean;
  /** Rendered inside the phone drawer, which has its own header and its own width. */
  embedded?: boolean;
}) {
  const { t } = useTranslation();
  const { isAdmin } = useAuth();

  return (
    <nav
      aria-label={t('a11y.mainNavigation')}
      className={cn(
        'flex h-full flex-col',
        embedded
          ? 'w-full'
          : cn(
              'border-r border-border bg-bg-subtle transition-[width] duration-200',
              collapsed ? 'w-sidebar-collapsed' : 'w-sidebar',
            ),
      )}
    >
      {!embedded && (
        <div
          className={cn(
            'flex h-topbar shrink-0 items-center border-b border-border',
            collapsed ? 'justify-center px-0' : 'gap-2 px-3',
          )}
        >
          <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded bg-accent text-accent-fg">
            <Scissors className="h-4 w-4" />
          </div>
          {!collapsed && <span className="truncate font-semibold tracking-tight">VideoGrab</span>}
        </div>
      )}

      <div className="shrink-0 border-b border-border p-2">
        <WorkspaceSwitcher collapsed={collapsed} />
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-2 pb-3">
        <Group titleKey="nav.groupWork" items={WORK_ITEMS} collapsed={collapsed} />
        <Group titleKey="nav.groupResults" items={RESULT_ITEMS} collapsed={collapsed} />
        {isAdmin && <Group titleKey="nav.groupAdmin" items={ADMIN_ITEMS} collapsed={collapsed} />}
      </div>

      {!hideToggle && (
      <div className="shrink-0 border-t border-border p-2">
        <button
          type="button"
          onClick={onToggle}
          aria-label={collapsed ? t('nav.expand') : t('nav.collapse')}
          className={cn(
            'flex w-full cursor-pointer items-center gap-2 rounded px-2.5 py-2 text-sm text-fg-subtle',
            'transition-colors duration-150 hover:bg-surface-hover hover:text-fg',
            collapsed && 'justify-center px-0',
          )}
        >
          <ChevronLeft className={cn('h-4 w-4 transition-transform', collapsed && 'rotate-180')} />
          {!collapsed && t('nav.collapse')}
        </button>
      </div>
      )}
    </nav>
  );
}

/** The phone layout: five destinations pinned to the bottom edge. */
export function MobileNav() {
  const { t } = useTranslation();
  const location = useLocation();

  const items: { to: string; labelKey: string; icon: React.ReactNode; prefix?: string }[] = [
    { to: routes.overview, labelKey: 'nav.overview', icon: <LayoutDashboard className="h-5 w-5" /> },
    { to: routes.download, labelKey: 'nav.download', icon: <Download className="h-5 w-5" /> },
    { to: routes.projects, labelKey: 'nav.projects', icon: <FolderOpen className="h-5 w-5" />, prefix: '/projects' },
    { to: routes.libraryDownloads, labelKey: 'nav.library', icon: <Library className="h-5 w-5" />, prefix: '/library' },
    { to: routes.activity, labelKey: 'nav.activity', icon: <Activity className="h-5 w-5" /> },
  ];

  return (
    <nav
      aria-label={t('a11y.mainNavigation')}
      className="fixed inset-x-0 bottom-0 z-30 flex border-t border-border bg-surface pb-[env(safe-area-inset-bottom)] md:hidden"
    >
      {items.map((item) => {
        const active = item.prefix
          ? location.pathname.startsWith(item.prefix)
          : location.pathname === item.to;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            aria-current={active ? 'page' : undefined}
            className={cn(
              // 44px minimum touch target.
              'flex min-h-[3.25rem] flex-1 cursor-pointer flex-col items-center justify-center gap-0.5 py-1.5',
              'text-2xs font-medium transition-colors duration-150',
              active ? 'text-accent' : 'text-fg-subtle hover:text-fg',
            )}
          >
            {item.icon}
            <span className="truncate px-0.5">{t(item.labelKey)}</span>
          </NavLink>
        );
      })}
    </nav>
  );
}
